import {isEqual} from "lodash-es";
import ObjectCopyService from "external-services/object-copy-service/index";
import {
  IBooking,
  ICustomer,
  IStaffNotRequired,
  ITableItem,
  StaffEditType,
  UnreliableBookingStatusIds
} from 'shared-types/index';
import { IStaffNew, NewBookingProps } from "./types";

const NS = 'StaffService';

class StaffService {

  public staffNameNotRequired({oldBooking, newBooking, staffEnabled, waitList, staffEditOptions, orgCustomer, updatedCustomer}: IStaffNotRequired): boolean {
    if (!staffEnabled || !oldBooking) {
      return true;
    }

    const isAdvancedStaffEdit = staffEnabled && (staffEditOptions ? staffEditOptions?.staffEditAll : true)
    let noChangesMade = true;

    const oldBookingWithoutStatus: string = ObjectCopyService.angularSafeJson(this.getBookingWithoutStatus(oldBooking, waitList)); // Removing the status object temporarily to test any other change in the object
    const newBookingWithoutStatus: string = ObjectCopyService.angularSafeJson(this.getBookingWithoutStatus(newBooking, waitList));
    if(isAdvancedStaffEdit) {
      // if (waitList) {
      //   return isEqual(oldBookingWithoutStatus, newBookingWithoutStatus);
      // }

      const hasCustomerNotesNoChange = isEqual(ObjectCopyService.angularSafeJson(orgCustomer?.notes), ObjectCopyService.angularSafeJson(updatedCustomer?.notes));
      const hasManagerNotesNoChange = isEqual(ObjectCopyService.angularSafeJson(orgCustomer?.managerNotes), ObjectCopyService.angularSafeJson(updatedCustomer?.managerNotes));
      // @todo: check if there are any problems with 'status_seated' id and dynamically created ids on older venues, like shorehouse on UAT. severity: mediunb  m
      return (isEqual(oldBookingWithoutStatus, newBookingWithoutStatus) && hasCustomerNotesNoChange && hasManagerNotesNoChange);// && newBooking.status._id === UnreliableBookingStatusIds.StatusSeated;
    }

    if (staffEditOptions?.staffEditPaxEdit && noChangesMade) {
      noChangesMade = isEqual(ObjectCopyService.angularSafeJson(oldBooking.people), ObjectCopyService.angularSafeJson(newBooking.people));
    }

    if (staffEditOptions?.staffEditNotesEdit && noChangesMade) {
      const notesNoChange = isEqual(ObjectCopyService.angularSafeJson(oldBooking.notes), ObjectCopyService.angularSafeJson(newBooking.notes));
      const hasCustomerNotesNoChange = isEqual(ObjectCopyService.angularSafeJson(orgCustomer?.notes), ObjectCopyService.angularSafeJson(updatedCustomer?.notes));
      const hasManagerNotesNoChange = isEqual(ObjectCopyService.angularSafeJson(orgCustomer?.managerNotes), ObjectCopyService.angularSafeJson(updatedCustomer?.managerNotes));

      noChangesMade = notesNoChange && hasCustomerNotesNoChange && hasManagerNotesNoChange;
    }

    if (staffEditOptions?.staffEditTableEdit && noChangesMade) {
      // cause the table object changes even if no changes are made
      const oldBookingTableIds = oldBooking.tables.map(t=>t._id)
      const newBookingTableIds = newBooking.tables.map(t=>t._id)
      noChangesMade = isEqual(ObjectCopyService.angularSafeJson(oldBookingTableIds), ObjectCopyService.angularSafeJson(newBookingTableIds));
    }

    if (staffEditOptions?.staffEditTimeEdit && noChangesMade) {
      noChangesMade = isEqual(ObjectCopyService.angularSafeJson(oldBooking.time), ObjectCopyService.angularSafeJson(newBooking.time));
    }

    if (staffEditOptions?.staffEditStatusEdit && noChangesMade) {
      const newStatus = newBooking.status
      if (newStatus._id === UnreliableBookingStatusIds.StatusCancelled || newStatus._id === UnreliableBookingStatusIds.StatusCancelledNoShow) {
        noChangesMade = isEqual(ObjectCopyService.angularSafeJson(oldBooking.status), ObjectCopyService.angularSafeJson(newBooking.status));
      }
      else {
        noChangesMade = true
      }
    }

    if (staffEditOptions?.staffEditPaymentBOEdit && noChangesMade) {
      const isPaymentSame = isEqual(ObjectCopyService.angularSafeJson(oldBooking.paymentDetails), ObjectCopyService.angularSafeJson(newBooking.paymentDetails));
      const isBoSame = isEqual(ObjectCopyService.angularSafeJson(oldBooking.selectedMenuOptions), ObjectCopyService.angularSafeJson(newBooking.selectedMenuOptions));

      noChangesMade = isPaymentSame && isBoSame
    }

    return noChangesMade;
  }

  private getBookingWithoutStatus(booking: IBooking, waitList: boolean): IBooking {
    const bookingWithoutStatus: IBooking = ObjectCopyService.angularSafeCopy(booking);
    if (bookingWithoutStatus.tables) {
      bookingWithoutStatus.tables.map((table: ITableItem) => {
        delete table.problemReasons
        delete table.free
        delete table.valid
        delete (table as any).isAltBg
    });
    }
    delete bookingWithoutStatus.time
    delete bookingWithoutStatus.selectedMenuOptions
    waitList ? delete bookingWithoutStatus.tables && delete bookingWithoutStatus.time && delete bookingWithoutStatus.people : null; // For waitList time and table and status are not mandatory to check. This is being used so that the old object and the new object are same.
    delete bookingWithoutStatus.status && delete bookingWithoutStatus.serviceIdOverRide && delete bookingWithoutStatus.serviceId && delete bookingWithoutStatus._rev && delete bookingWithoutStatus.customersCovid && delete bookingWithoutStatus.bookingType;
    if (bookingWithoutStatus.additionalPaymentDetails && bookingWithoutStatus.additionalPaymentDetails.length === 0) {
      delete bookingWithoutStatus.additionalPaymentDetails;
    }
    return bookingWithoutStatus;
  }

  public checkValidityForStaffName(staffEditRequired: boolean, staffEditEnabled: boolean, staffEditOptions: StaffEditType, staffEditTypeVal: boolean) {
    const isNewStaffEditSettings = staffEditRequired && staffEditOptions;
    return (staffEditRequired && staffEditEnabled) ? (isNewStaffEditSettings ? staffEditTypeVal : true) : false
  }

  public getNewBookingValidation(settings: {staffEnabled?: boolean, staffRequired?: boolean, staffNew?: IStaffNew}, prop: NewBookingProps, isWalkin: boolean) {
    // venue still have the old settings enabled, in legacy setting Staff is not required when walkin booking
    if (!settings.hasOwnProperty('staffNew')) {
      // Old version did not check changingDate
      if (prop === 'changingDate') {
        return false
      }
      const resultOldSetting = (settings?.staffEnabled && settings?.staffRequired && !isWalkin ? true : false)
      // console.debug('staffValidationSlice', {resultOldSetting})
      return resultOldSetting
    }
    if (settings?.staffEnabled && settings?.staffRequired) {
      const resultFromNewSetting = Boolean(settings.staffNew[prop]);
      // console.debug('staffValidationSlice', {resultFromNewSetting})
      return resultFromNewSetting
    }
    return false;
  }
}

const instance = new StaffService();
export default instance as StaffService;
